import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { NodeData } from 'json-edit-react';
import { RootState } from '../../../redux';
import {
  canRenderTable,
  getColumnName,
  getTableName,
  getUniqueTableName,
  isArray,
  isPrimitive,
} from './tableValidation';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import NodeTable from './NodeTable';
import { palette } from '../../..';
import AddTaskIcon from '@mui/icons-material/AddTask';

interface JSONCustomButtonProps {
  nodeData: NodeData;
  selectedNodePaths: Set<string>;
  setSelectedNodePaths: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * @returns custom icon for each json node
 * return NodeTable.tsx component on preview(eye icon)
 * eye icon is visible only when table is allowed(rules table validation are inside tableValidation.ts file)
 * shows '❌' if node is already selected and shows '✅' if we node is not present in canvas(If table is valid then it shows text 'Save this node' in tooltip if not then 'Cannot create a table from this node'(table validation rules are inside tableValidation.ts file))
 */
const JSONCustomButton = ({
  nodeData,
  selectedNodePaths,
  setSelectedNodePaths,
}: JSONCustomButtonProps) => {
  const [showTableData, setShowTableData] = useState(false);

  const { selectedTables, selectedConnection } = useSelector((state: RootState) => ({
    selectedTables: state.dataset.selectedTables,
    selectedConnection: state.dataset.connections.find(
      (connection) => connection.id === state.dataset.selectedConnectionId,
    ),
  }));

  const { value, path, parentData } = nodeData;

  // existingTablenames has set of selected table names which are present on canvas (has set of all the table names regardless of source)
  const existingTableNames = useMemo(
    () => new Set(selectedTables.map((selectedTable) => selectedTable.table)),
    [selectedTables],
  );

  // has key value pair of jsonPath with table name(only web api table names)
  const canvasTableNameByPath = useMemo(() => {
    const map = new Map<string, string>();
    if (!selectedConnection) return map;
    selectedTables
      .filter((table) => table.connectionId === selectedConnection.id && table.isWebApi === true)
      .forEach((table) => {
        if (table.customQuery) {
          const parsedCustomQuery = JSON.parse(table.customQuery);
          if (Array.isArray(parsedCustomQuery?.jsonPath)) {
            map.set(JSON.stringify(parsedCustomQuery.jsonPath), table.table);
          }
        }
      });
    return map;
  }, [selectedTables, selectedConnection?.id]);

  if (isPrimitive(value) && Array.isArray(parentData)) return null;

  const jsonPath: (string | number)[] =
    path.length === 0
      ? ['response']
      : (path[0] as string) === 'response'
        ? path
        : ['response', ...path];

  const pathKey = JSON.stringify(jsonPath);

  const isTableAllowed = useMemo(() => canRenderTable(value), [value]);
  const isOnCanvas = selectedNodePaths.has(pathKey);

  const toggleOnCanvas = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isTableAllowed) return;
    setSelectedNodePaths((prev) => {
      const next = new Set<string>(prev);
      next.has(pathKey) ? next.delete(pathKey) : next.add(pathKey);
      return next;
    });
  };

  // table name(nearest node name) without following any hard logic
  const baseTableName = getTableName(path, value);

  /**
   * it contains table name on preview(confirmed name or possible name)
   * If a node is on canvas then it uses already assigned name in canvas.
   * If node is not on the canvas, generate a unique name by appending _number to avoid conflicts in table name on the canvas for non root node or if root node then it assigns root name which is fixed
   */
  const previewTableName = isOnCanvas
    ? (canvasTableNameByPath.get(pathKey) ?? baseTableName)
    : getUniqueTableName(baseTableName, existingTableNames);

  const rowCount = isArray(value) ? value.length : 1;

  return (
    <>
      <span
        style={{
          marginLeft: '1.75rem',
          fontSize: '0.85em',
          display: 'flex',
          flexWrap: 'nowrap',
          gap: '2px',
        }}
      >
        {isOnCanvas && isTableAllowed ? (
          <Tooltip
            title="Remove this node"
            arrow
            placement="top"
            disableInteractive
            slotProps={{ tooltip: { className: 'tooltip-font' } }}
          >
            <CloseIcon onClick={toggleOnCanvas} sx={{ color: 'red', fontSize: '12px' }} />
          </Tooltip>
        ) : !isOnCanvas ? (
          <Tooltip
            title={isTableAllowed ? 'Save this node' : 'Cannot create a table from this node'}
            arrow
            placement="top"
            disableInteractive
            slotProps={{ tooltip: { className: 'tooltip-font' } }}
          >
            <AddTaskIcon
              onClick={toggleOnCanvas}
              sx={{ color: palette.primary.main, fontSize: '12px' }}
            />
          </Tooltip>
        ) : null}
        {isTableAllowed && (
          <Tooltip
            title="View Table"
            arrow
            placement="top"
            disableInteractive
            slotProps={{ tooltip: { className: 'tooltip-font' } }}
          >
            <VisibilityOutlinedIcon
              onClick={(e) => {
                e.stopPropagation();
                setShowTableData(true);
              }}
              sx={{ color: palette.secondary.light, fontSize: '12px' }}
            />
          </Tooltip>
        )}
      </span>

      {/* Modal to preview table data */}
      <Dialog
        open={showTableData}
        onClose={() => setShowTableData(false)}
        maxWidth="xl"
        fullWidth
        slotProps={{ paper: { sx: { minHeight: '90%' } } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            flexDirection: 'row',
            columnGap: '2rem',
            justifyContent: 'space-between',
            fontSize: '16px',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: '16px' }}>Table: {previewTableName}</Typography>
          <Typography sx={{ fontSize: '16px' }}>Rows Displayed: {rowCount}</Typography>
          <IconButton size="small" onClick={() => setShowTableData(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            maxWidth: 'fit-content',
            '::-webkit-scrollbar': { width: '5px', height: '5px' },
          }}
        >
          <Box
            sx={{
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: '60vh',
              '&::-webkit-scrollbar': { width: '5px', height: '5px' },
              '&::-webkit-scrollbar-thumb': { background: '#b2b2b2' },
            }}
          >
            <NodeTable data={value} columnName={getColumnName(path)} />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default JSONCustomButton;
